/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, { useState } from "react";
import Button from "@mui/material/Button";
import summaryBoxStyle from "./summaryBoxStyle";
import Grid from "@mui/material/Grid";
import "moment/locale/de";
import iconData from "../../../../assets/icons/generic/toggleable/IconData";
import Typography from "@mui/material/Typography";
import ContentBox from "../../../Box/ContentBox/ContentBox";
import CaseDetailsItem from "../../../Serviceworld/Common/CaseDetailsItem/CaseDetailsItem";
import { translate } from "../../../../services/translations/translations";
import { AbstractCase } from "../../../../types/Entities/AbstractCase";
import {Divider} from "@mui/material";

type SummaryBox = {
  product: AbstractCase;
};

export default function SummaryBox({ product }: SummaryBox) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <ContentBox headline={"Meine Angaben"} icon={iconData}>
      <Typography variant={"h6"} sx={summaryBoxStyle.subheader}>
        Kündigung
      </Typography>
      <Divider sx={summaryBoxStyle.divider} />
      <CaseDetailsItem caseData={product} fieldPath={"contractTerminated"} />
      {product.contractTerminated && (
        <>
          <CaseDetailsItem caseData={product} fieldPath={"contractTerminationReceivedDate"} fieldType={"date"} />
          <CaseDetailsItem caseData={product} fieldPath={"terminationNoticeType"} />
        </>
      )}
      <CaseDetailsItem caseData={product} fieldPath={"preferredOutcome"} />
      <Grid container item xs={12}>
        <Grid item xs={12} sx={summaryBoxStyle.label}>
          {translate("settlement.terminationObstacles.label", product)}
        </Grid>
        <Grid item xs={12}>
          {product.terminationObstacles.length
            ? product.terminationObstacles.map((value: string) => translate(`settlement.terminationObstacles.options.${value}.label`, product)).join(", ")
            : "Kein besonderer Kündigungsschutz"}
        </Grid>
      </Grid>
      {isExpanded && (
        <div>
          <Typography variant={"h6"} sx={summaryBoxStyle.subheader}>
            Arbeitsverhältnis
          </Typography>
          <Divider sx={summaryBoxStyle.divider} />
          <Grid container>
            <CaseDetailsItem caseData={product} fieldPath={"employedSince"} fieldType={"dateMY"} />
            <CaseDetailsItem caseData={product} fieldPath={"grossSalary"} fieldType={"money"} />
            <CaseDetailsItem caseData={product} fieldPath={"jobDescription"} />
            <CaseDetailsItem caseData={product} fieldPath={"numberOfEmployees"} />
            <CaseDetailsItem caseData={product} fieldPath={"employer.worksCouncil"} />
          </Grid>

          <Typography variant={"h6"} sx={summaryBoxStyle.subheader}>
            Persönliche Angaben
          </Typography>
          <Divider sx={summaryBoxStyle.divider} />
          <Grid container>
            <CaseDetailsItem caseData={product} fieldPath={"client.givenName"} />
            <CaseDetailsItem caseData={product} fieldPath={"client.familyName"} />
            <CaseDetailsItem caseData={product} fieldPath={"client.birthDate"} fieldType={"date"} />
            <CaseDetailsItem caseData={product} fieldPath={"client.residenceAddress.streetAddress"} />
            <CaseDetailsItem caseData={product} fieldPath={"client.residenceAddress.postalCode"} />
            <CaseDetailsItem caseData={product} fieldPath={"client.residenceAddress.addressLocality"} />
            <CaseDetailsItem caseData={product} fieldPath={"customer.telephone"} />
            <CaseDetailsItem caseData={product} fieldPath={"customer.contactTime"} />
            <CaseDetailsItem caseData={product} fieldPath={"maritalStatus"} />
            {product.maritalStatus === "married" && <CaseDetailsItem caseData={product} fieldPath={"partnerIsWorking"} />}
            <CaseDetailsItem caseData={product} fieldPath={"numberChildren"} />
          </Grid>

          <Typography variant={"h6"} sx={summaryBoxStyle.subheader}>
            Versicherung
          </Typography>
          <CaseDetailsItem caseData={product} fieldPath={"legalExpenseInsurance"} />

          {product.legalExpenseInsurance && (
            <>
              <CaseDetailsItem caseData={product} fieldPath={"insurance.insurance.name"} />
              <CaseDetailsItem caseData={product} fieldPath={"insurance.insurancePolicyNumber"} />
              <CaseDetailsItem caseData={product} fieldPath={"insurance.caseReferenceNumber"} />
              <CaseDetailsItem caseData={product} fieldPath={"insurance.policyHolderType"} />
              {product.insurance.policyHolderType === "familyMember" && (
                <>
                  <CaseDetailsItem caseData={product} fieldPath={"insurance.policyHolder.gender"} labelPostfix={"Versicherter"} />
                  <CaseDetailsItem caseData={product} fieldPath={"insurance.policyHolder.givenName"} labelPostfix={"Versicherter"} />
                  <CaseDetailsItem caseData={product} fieldPath={"insurance.policyHolder.familyName"} labelPostfix={"Versicherter"} />
                </>
              )}
            </>
          )}
        </div>
      )}
      <Button fullWidth color={"primary"} onClick={() => setIsExpanded(!isExpanded)}>
        {isExpanded ? "Weniger anzeigen" : "Alle anzeigen"}
      </Button>
    </ContentBox>
  );
}
